<template>
  <div id="live-feed">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: getSectionName, link: `${pathPrefix}/live-feed` },
      ]"
    />
    <div class="page-container">
      <div
        v-if="filters && filters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.selectLiveFeed')"
          data-testid="filter-trigger-live-feed"
          @change="handleFilterFeeds"
        >
          <option
            v-for="filter in filters"
            :key="filter.id"
            :value="filter.id"
          >
            {{ filter.name }}
          </option>
        </select>
      </div>
      <LoadingIndicator v-if="isLoading" />
      <div
        v-for="(liveFeed, idx) in feeds"
        v-else
        :id="liveFeed.id"
        :key="idx"
        class="page-container-item"
      >
        <div class="avatar-holder">
          <img
            alt=""
            :src="liveFeed.avatar"
            @error="handleAvatarError"
          />
        </div>
        <div
          class="status"
          v-html="liveFeed.status"
        />
        <div class="date-author">{{ liveFeed.time_ago }}, {{ liveFeed.author }}</div>
        <div
          v-if="liveFeed.live_feed_images.length > 0"
          class="images"
        >
          <ts-row class="images-row">
            <ts-col
              v-for="(image, index) in liveFeed.live_feed_images"
              :key="index"
              class="image-wrapper-container"
              ts-align="center"
            >
              <div class="image-wrapper">
                <img
                  :src="image.url"
                  :alt="image.alt_text"
                  @click="handleOpenLightbox($event, liveFeed.live_feed_images, index)"
                />
              </div>
            </ts-col>
          </ts-row>
        </div>
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import { useHead, useAsyncData, useNuxtApp, navigateTo, useRoute } from '#imports';
import { useStore } from 'vuex';
import { useLightboxStore } from '@/stores/lightboxStore';
import tsCol from '../../components/global/tsCol.vue';
import tsRow from '../../components/global/tsRow.vue';
import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Pagination from '../../components/global/Pagination.vue';
import { setupAndFetchLiveFeed } from '../../helpers/section/sections.helper';
import { getMetadataAndTags } from '../../helpers/metadata';

defineOptions({ name: 'LiveFeed' });

const store = useStore();
const lightboxStore = useLightboxStore();
const route = useRoute();
const { $axios, $t } = useNuxtApp();

const isLoading = ref(false);
const feeds = ref([]);
const filters = ref([]);
const totalPages = ref(1);
const selectedFilter = ref('');
const currentPage = ref(1);

const { data: initialData } = await useAsyncData('live-feed', () =>
  setupAndFetchLiveFeed(store, route.query, $axios, $t),
);

if (initialData.value) {
  feeds.value = initialData.value.feeds ?? [];
  selectedFilter.value = initialData.value.selectedFilter ?? '';
  filters.value = initialData.value.filters ?? [];
  totalPages.value = initialData.value.totalPages ?? 1;
  currentPage.value = initialData.value.currentPage ?? 1;
}

const metaDataInfo = getMetadataAndTags(store.state.settings.metadata, 'live_feeds', store.getters.getSectionName);
useHead({
  ...metaDataInfo.metaTitle,
  meta: metaDataInfo.metaTags,
});

const globals = computed(() => store.state.globals);
const pathPrefix = computed(() => store.state.pathPrefix);
const getSectionName = computed(() => store.getters.getSectionName);

watch(
  () => route.query.page_no,
  (newVal, oldVal) => {
    if (import.meta.client && oldVal !== newVal) {
      window.scrollTo(0, 0);
    }
  },
);

onBeforeRouteUpdate((to, from, next) => {
  if (
    import.meta.client &&
    (to.query.page_no !== from.query.page_no ||
      to.query.filter_id !== from.query.filter_id ||
      to.query.page_size !== from.query.page_size)
  ) {
    fetchLiveFeed(to.query);
  }
  next();
});

async function fetchLiveFeed(query) {
  isLoading.value = true;
  totalPages.value = 0;
  const result = await setupAndFetchLiveFeed(store, query, $axios, $t);
  feeds.value = result.feeds;
  selectedFilter.value = result.selectedFilter;
  filters.value = result.filters;
  totalPages.value = result.totalPages;
  currentPage.value = result.currentPage;
  isLoading.value = false;
}

function handleAvatarError(evt) {
  const img = evt.target;
  if (img instanceof HTMLImageElement) {
    img.src = 'https://www.apptegy.net/fallback/circled_default_avatar.png';
  }
}

function handleFilterFeeds() {
  return navigateTo({ query: { filter_id: selectedFilter.value } });
}

function handleOpenLightbox(e, images, startingIndex) {
  lightboxStore.setPrevFocusedElem(e.target);
  lightboxStore.setLightBoxCurrentIndex(startingIndex);
  lightboxStore.openLightbox();
  lightboxStore.setLightboxImages(images);
}
</script>

<style scoped lang="scss">
#live-feed {
  .page-container {
    max-width: 640px;
  }
  .page-container-item {
    padding: 10px 0;
  }
  .avatar-holder {
    float: left;
    margin: 0 15px 0 0;
    img {
      width: 70px;
      max-width: 100%;
      height: 70px;
      margin-top: 0;
      object-fit: contain;
    }
  }
  .status {
    font-size: 16px;
    :deep(a) {
      color: var(--links-color);
    }
  }
  .date-author {
    color: #595959;
    font-size: 12px;
    text-transform: uppercase;
    margin: 10px 0;
  }
  .images {
    width: 100%;
    overflow: hidden;
    font-size: 0;
    margin-top: 50px;
    margin-bottom: 25px;
    .images-row {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      .image-wrapper-container {
        flex: 1 1;
        min-width: 33%;
      }
    }
    .image-wrapper {
      margin: 5px;
      overflow: hidden;
      background-color: #f3f3f3;
      border-radius: 4px;
      img {
        width: 100%;
        aspect-ratio: 1/1;
        object-fit: contain;
        cursor: pointer;
      }
    }
  }
}
</style>
